/**
 * Executive Insights Module Configuration
 *
 * High-level dashboards for leadership with KPIs, OKRs,
 * ROI analysis, and strategic reporting.
 */

import { ModuleConfig } from './moduleTypes'

export const executiveConfig: ModuleConfig = {
  id: 'executive',
  name: 'Executive Insights',
  description: 'Strategic dashboards and leadership reporting',
  icon: 'Briefcase',
  basePath: '/executive',
  enabledIndustries: ['manufacturing', 'oil_gas', 'healthcare', 'financial', 'energy', 'transportation', 'mining', 'chemicals', 'food_beverage', 'pharmaceuticals', 'aerospace', 'metals', 'iot_telecom', 'utilities', 'pulp_paper', 'automotive', 'datacenter', 'facility_management',
    // Phase 4 — non-industrial + medical-grade verticals (2026-06-27)
    'aquaculture_lobster', 'aquaculture_salmon', 'aquaculture_shrimp',
    'biosensor_ide_wearable', 'biosensor_continuous_glucose', 'biosensor_oximeter_fleet',
    'wearable_etd'],

  subPages: [
    {
      id: 'summary',
      path: '',
      name: 'Executive Summary',
      icon: 'FileText',
      description: 'High-level overview of operations and key metrics',
      component: 'ExecutiveSummary',
      requiredPermissions: ['executive:read'],
      position: 1,
      industryLabels: {
        healthcare: 'Clinical Operations Summary',
        financial: 'Portfolio Summary',
        transportation: 'Fleet Operations Summary',
        iot_telecom: 'Global Connectivity Summary',
      },
    },
    {
      id: 'okr',
      path: 'okr',
      name: 'OKR Dashboard',
      icon: 'Target',
      description: 'Objectives and key results tracking',
      component: 'OKRDashboard',
      requiredPermissions: ['executive:read'],
      position: 2,
    },
    {
      id: 'kpi',
      path: 'kpi',
      name: 'KPI Scorecards',
      icon: 'Gauge',
      description: 'Key performance indicator monitoring',
      component: 'KPIScorecard',
      requiredPermissions: ['executive:read'],
      position: 3,
    },
    {
      id: 'trends',
      path: 'trends',
      name: 'Trend Analysis',
      icon: 'TrendingUp',
      description: 'Long-term trend analysis and forecasting',
      component: 'TrendAnalysis',
      requiredPermissions: ['analytics:read'],
      position: 4,
    },
    {
      id: 'roi',
      path: 'roi',
      name: 'ROI & Value',
      icon: 'PiggyBank',
      description: 'Return on investment and value realization',
      component: 'ROIValueDashboard',
      requiredPermissions: ['executive:read'],
      position: 5,
    },
    {
      id: 'reports',
      path: 'reports',
      name: 'Report Generator',
      icon: 'Download',
      description: 'Generate and export executive reports',
      component: 'ExecutiveReportGenerator',
      requiredPermissions: ['reports:write'],
      position: 6,
    },
    {
      id: 'strategic',
      path: 'strategic',
      name: 'Strategic Insights',
      icon: 'Lightbulb',
      description: 'AI-generated strategic recommendations',
      component: 'StrategicInsights',
      requiredPermissions: ['executive:read'],
      position: 7,
    },
  ],

  workflows: [
    {
      id: 'executive-review',
      name: 'Executive Review',
      description: 'Comprehensive executive-level review',
      icon: 'Briefcase',
      steps: [
        {
          id: 'review-summary',
          name: 'Review Summary',
          description: 'Start with high-level executive summary',
          navigateTo: 'summary',
          icon: 'FileText',
          estimatedDuration: 5,
        },
        {
          id: 'check-okrs',
          name: 'Check OKR Progress',
          description: 'Review progress against objectives',
          navigateTo: 'okr',
          icon: 'Target',
          estimatedDuration: 5,
        },
        {
          id: 'review-kpis',
          name: 'Review KPI Scorecards',
          description: 'Analyze key performance indicators',
          navigateTo: 'kpi',
          icon: 'Gauge',
          estimatedDuration: 10,
        },
        {
          id: 'analyze-trends',
          name: 'Analyze Trends',
          description: 'Review long-term trends and patterns',
          navigateTo: 'trends',
          icon: 'TrendingUp',
          estimatedDuration: 10,
        },
        {
          id: 'assess-roi',
          name: 'Assess ROI',
          description: 'Evaluate return on investment',
          navigateTo: 'roi',
          icon: 'PiggyBank',
          estimatedDuration: 10,
        },
        {
          id: 'review-insights',
          name: 'Review Strategic Insights',
          description: 'Consider AI-generated recommendations',
          navigateTo: 'strategic',
          icon: 'Lightbulb',
          estimatedDuration: 10,
        },
      ],
      triggerConditions: ['weeklyReview', 'monthlyReview', 'quarterlyReview'],
    },
    {
      id: 'report-generation',
      name: 'Report Generation',
      description: 'Generate executive reports',
      icon: 'FileDown',
      steps: [
        {
          id: 'select-period',
          name: 'Select Report Period',
          description: 'Choose the reporting time period',
          icon: 'Calendar',
          estimatedDuration: 2,
        },
        {
          id: 'review-data',
          name: 'Review Data Sources',
          description: 'Verify data accuracy and completeness',
          navigateTo: 'kpi',
          icon: 'Database',
          estimatedDuration: 5,
        },
        {
          id: 'customize-report',
          name: 'Customize Report',
          description: 'Select sections and metrics to include',
          navigateTo: 'reports',
          icon: 'Settings',
          estimatedDuration: 5,
        },
        {
          id: 'generate-export',
          name: 'Generate & Export',
          description: 'Create report in desired format',
          icon: 'Download',
          estimatedDuration: 3,
        },
        {
          id: 'distribute',
          name: 'Distribute Report',
          description: 'Share with stakeholders',
          icon: 'Share2',
          estimatedDuration: 2,
        },
      ],
      triggerConditions: ['reportingDeadline', 'boardMeeting'],
    },
  ],

  narratives: {
    sections: [
      {
        id: 'kpi-summary',
        name: 'Key Performance Summary',
        template: 'Period: {reportPeriod}. Fleet reliability: {reliabilityPercent}%. Model accuracy: {accuracyPercent}%. Downtime: {downtimeHours} hours ({downtimeChangePercent}% vs previous). Cost avoidance: {costAvoidance}.',
        order: 1,
        required: true,
      },
      {
        id: 'financial-impact',
        name: 'Financial Impact',
        template: '**Financial Impact:**\n• Total savings: {totalSavings}\n• ROI: {roiPercent}%\n• Payback period: {paybackPeriod}',
        order: 2,
      },
      {
        id: 'key-wins',
        name: 'Key Wins',
        template: '**Key Wins:**\n• {win1}\n• {win2}\n• {win3}',
        order: 3,
      },
      {
        id: 'attention-areas',
        name: 'Areas Requiring Attention',
        template: '**Areas Requiring Attention:**\n• {attention1}\n• {attention2}',
        order: 4,
        condition: 'hasAttentionItems',
      },
      {
        id: 'strategic-recommendations',
        name: 'Strategic Recommendations',
        template: '**Strategic Recommendations:**\n1. {recommendation1}\n2. {recommendation2}\n3. {recommendation3}',
        order: 5,
        required: true,
      },
    ],
    availableVariables: [
      'reportPeriod', 'reliabilityPercent', 'accuracyPercent', 'downtimeHours', 'downtimeChangePercent', 'costAvoidance',
      'totalSavings', 'roiPercent', 'paybackPeriod',
      'win1', 'win2', 'win3',
      'attention1', 'attention2',
      'recommendation1', 'recommendation2', 'recommendation3',
    ],
  },

  industryOverrides: {
    healthcare: {
      name: 'Clinical Operations Insights',
      description: 'Clinical operations performance and compliance reporting',
      icon: 'HeartPulse',
      additionalSubPages: [
        {
          id: 'compliance-report',
          path: 'compliance',
          name: 'Compliance Report',
          icon: 'Shield',
          description: 'Regulatory compliance status and reporting',
          component: 'ComplianceReport',
          position: 8,
        },
        {
          id: 'patient-safety',
          path: 'safety',
          name: 'Patient Safety Metrics',
          icon: 'Heart',
          description: 'Device-related patient safety indicators',
          component: 'PatientSafetyMetrics',
          position: 9,
        },
      ],
    },
    financial: {
      name: 'Portfolio Intelligence',
      description: 'System portfolio performance and risk reporting',
      icon: 'Building2',
      additionalSubPages: [
        {
          id: 'risk-report',
          path: 'risk',
          name: 'Risk Report',
          icon: 'AlertTriangle',
          description: 'Portfolio risk exposure report',
          component: 'RiskReport',
          position: 8,
        },
        {
          id: 'sla-compliance',
          path: 'sla',
          name: 'SLA Compliance',
          icon: 'CheckCircle',
          description: 'Service level agreement compliance',
          component: 'SLACompliance',
          position: 9,
        },
      ],
    },
    transportation: {
      name: 'Fleet Operations Insights',
      description: 'Fleet performance and efficiency reporting',
      icon: 'Truck',
      additionalSubPages: [
        {
          id: 'fleet-efficiency',
          path: 'efficiency',
          name: 'Fleet Efficiency',
          icon: 'Fuel',
          description: 'Fuel efficiency and utilization metrics',
          component: 'FleetEfficiency',
          position: 8,
        },
        {
          id: 'driver-performance',
          path: 'drivers',
          name: 'Driver Performance',
          icon: 'Users',
          description: 'Driver performance and safety metrics',
          component: 'DriverPerformanceReport',
          position: 9,
        },
      ],
    },
    iot_telecom: {
      name: 'Global Connectivity Insights',
      description: 'VIoT-Flex Multi-IMSI fleet performance and cost intelligence',
      icon: 'Globe',
      additionalSubPages: [
        {
          id: 'connectivity-kpi',
          path: 'connectivity',
          name: 'Connectivity KPIs',
          icon: 'Signal',
          description: 'Fleet-wide connectivity and uptime metrics',
          component: 'ConnectivityKPIDashboard',
          position: 8,
        },
        {
          id: 'carrier-performance',
          path: 'carriers',
          name: 'Carrier Performance',
          icon: 'Radio',
          description: 'Multi-IMSI carrier performance scorecard',
          component: 'CarrierPerformanceScorecard',
          position: 9,
        },
        {
          id: 'global-coverage',
          path: 'coverage',
          name: 'Global Coverage',
          icon: 'Globe',
          description: 'Geographic coverage analysis across regions',
          component: 'GlobalCoverageAnalysis',
          position: 10,
        },
        {
          id: 'cost-intelligence',
          path: 'cost',
          name: 'Cost Intelligence',
          icon: 'DollarSign',
          description: 'Connectivity cost analysis and optimization',
          component: 'CostIntelligence',
          position: 11,
        },
        {
          id: 'regulatory-compliance',
          path: 'compliance',
          name: 'Regulatory Compliance',
          icon: 'Shield',
          description: 'Global regulatory compliance status',
          component: 'RegulatoryComplianceReport',
          position: 12,
        },
        {
          id: 'multi-imsi-analytics',
          path: 'multi-imsi',
          name: 'Multi-IMSI Analytics',
          icon: 'Layers',
          description: 'eUICC and Multi-IMSI utilization insights',
          component: 'MultiIMSIAnalytics',
          position: 13,
        },
        {
          id: 'fleet-growth',
          path: 'growth',
          name: 'Fleet Growth',
          icon: 'TrendingUp',
          description: 'SIM fleet growth and deployment metrics',
          component: 'FleetGrowthAnalysis',
          position: 14,
        },
        {
          id: 'vendor-comparison',
          path: 'vendors',
          name: 'Vendor Comparison',
          icon: 'Building2',
          description: 'Compare connectivity vendor performance',
          component: 'VendorComparison',
          position: 15,
        },
      ],
      additionalWorkflows: [
        {
          id: 'monthly-business-review',
          name: 'Monthly Business Review',
          description: 'Comprehensive monthly connectivity operations review',
          icon: 'Briefcase',
          steps: [
            {
              id: 'review-kpis',
              name: 'Review Connectivity KPIs',
              description: 'Check fleet-wide connectivity metrics',
              navigateTo: 'connectivity-kpi',
              icon: 'Signal',
              estimatedDuration: 10,
            },
            {
              id: 'analyze-carriers',
              name: 'Analyze Carrier Performance',
              description: 'Review carrier performance scorecards',
              navigateTo: 'carrier-performance',
              icon: 'Radio',
              estimatedDuration: 10,
            },
            {
              id: 'review-costs',
              name: 'Review Cost Intelligence',
              description: 'Analyze connectivity costs and savings',
              navigateTo: 'cost-intelligence',
              icon: 'DollarSign',
              estimatedDuration: 10,
            },
            {
              id: 'check-compliance',
              name: 'Check Regulatory Compliance',
              description: 'Verify global regulatory status',
              navigateTo: 'regulatory-compliance',
              icon: 'Shield',
              estimatedDuration: 5,
            },
            {
              id: 'review-growth',
              name: 'Review Fleet Growth',
              description: 'Track deployment and expansion metrics',
              navigateTo: 'fleet-growth',
              icon: 'TrendingUp',
              estimatedDuration: 5,
            },
            {
              id: 'generate-report',
              name: 'Generate Executive Report',
              description: 'Create board-ready summary report',
              navigateTo: 'reports',
              icon: 'Download',
              estimatedDuration: 10,
            },
          ],
          triggerConditions: ['monthlyReview', 'quarterlyReview', 'boardMeeting'],
        },
        {
          id: 'cost-optimization-review',
          name: 'Cost Optimization Review',
          description: 'Strategic connectivity cost reduction planning',
          icon: 'DollarSign',
          steps: [
            {
              id: 'analyze-costs',
              name: 'Analyze Current Costs',
              description: 'Deep dive into connectivity spend',
              navigateTo: 'cost-intelligence',
              icon: 'DollarSign',
              estimatedDuration: 15,
            },
            {
              id: 'compare-vendors',
              name: 'Compare Vendors',
              description: 'Review vendor pricing and performance',
              navigateTo: 'vendor-comparison',
              icon: 'Building2',
              estimatedDuration: 10,
            },
            {
              id: 'analyze-multi-imsi',
              name: 'Analyze Multi-IMSI Usage',
              description: 'Review profile switching efficiency',
              navigateTo: 'multi-imsi-analytics',
              icon: 'Layers',
              estimatedDuration: 10,
            },
            {
              id: 'review-coverage',
              name: 'Review Coverage Efficiency',
              description: 'Identify coverage optimization opportunities',
              navigateTo: 'global-coverage',
              icon: 'Globe',
              estimatedDuration: 10,
            },
            {
              id: 'strategic-plan',
              name: 'Create Strategic Plan',
              description: 'Document cost reduction recommendations',
              navigateTo: 'strategic',
              icon: 'Lightbulb',
              estimatedDuration: 15,
            },
          ],
          triggerConditions: ['quarterlyReview', 'budgetPlanning', 'contractNegotiation'],
        },
        {
          id: 'global-expansion-planning',
          name: 'Global Expansion Planning',
          description: 'Plan connectivity for new geographic regions',
          icon: 'Globe',
          steps: [
            {
              id: 'analyze-coverage',
              name: 'Analyze Target Coverage',
              description: 'Review coverage options for new regions',
              navigateTo: 'global-coverage',
              icon: 'Globe',
              estimatedDuration: 10,
            },
            {
              id: 'compare-carriers',
              name: 'Compare Regional Carriers',
              description: 'Evaluate carrier options in target regions',
              navigateTo: 'carrier-performance',
              icon: 'Radio',
              estimatedDuration: 10,
            },
            {
              id: 'check-compliance',
              name: 'Check Regulatory Requirements',
              description: 'Review compliance needs for new regions',
              navigateTo: 'regulatory-compliance',
              icon: 'Shield',
              estimatedDuration: 10,
            },
            {
              id: 'project-costs',
              name: 'Project Expansion Costs',
              description: 'Estimate connectivity costs for expansion',
              navigateTo: 'cost-intelligence',
              icon: 'DollarSign',
              estimatedDuration: 10,
            },
            {
              id: 'create-plan',
              name: 'Create Expansion Plan',
              description: 'Document strategic expansion recommendations',
              navigateTo: 'strategic',
              icon: 'Lightbulb',
              estimatedDuration: 15,
            },
          ],
          triggerConditions: ['expansionPlanned', 'newMarketEntry', 'acquisitionIntegration'],
        },
      ],
    },
    datacenter: {
      name: 'Infrastructure Operations',
      description: 'Datacenter operations performance, PUE, and capacity reporting',
      icon: 'Server',
    },
  },

  requiredPermissions: ['executive:read'],
}
